import React, { useEffect } from 'react';
import { VendorAccountSetupStepFooter } from 'framework';
import { VendorAccountSetupStepLayout } from 'framework';
import { useWebstoreContext } from 'vendor/contexts/WebstoreContext/WebstoreContext';
import { useWebstoreSetupWizard } from 'vendor/components/WebstoreSetup/NewWebstoreSetupContext';
import { NewVendorWebstoreSetupHeader } from '../../NewVendorWebstoreSetupHeader';
import SuccessPage from 'framework/components/Webstore/SuccessPage/SuccessPage';
import { Container, Email, Text } from './VendorPaymentSuccess.styled';
import { useTranslation } from 'react-i18next';
import { maskEmail } from 'shared/utils/utils';
import { useAuthUserContext } from 'contexts/AuthUserContext';
import { useTealium, buildWebstoreSetupStepPageData } from '../../../../../Tealium';
import PearlForm from 'vendor/components/forms/PearlForm';
import { StorageService } from 'lib/StorageService';
import { VendorService } from 'vendor/lib/apis/VendorService';
import { useStore } from 'stores/Store';

const formName = 'VendorPaymentSuccess';
const BLUECORE_NAMESPACE = 'davids_bridal_pearlconnect';

const buildBluecorePurchasePixelUrl = ({ email, phone, total, orderId, productId }) => {
    const params = new URLSearchParams({
        token: BLUECORE_NAMESPACE,
        email,
        total,
        order_id: orderId,
        product_ids: productId,
        phone_number: phone,
    });
    return `https://api.bluecore.app/api/track/purchase_pixel?${params.toString()}`;
};

// webstore.SelectedPlan values, same source PLAN_TIER_SELECTED (PostHog) uses
// in VendorBusinessDetailsFormNew.js -- see english.js Forms.PlanTier for the
// Free/Essential/Premium naming this maps to.
const MEMBERSHIP_TYPES_BY_SELECTED_PLAN = {
    planone: 'free',
    plantwo: 'essential',
    planthree: 'premium',
};

const trackConnectConversion = (membershipType) => {
    window.dataLayer = window.dataLayer || [];

    const events = {
        free: 'free_signup',
        essential: 'essential_subscription',
        premium: 'premium_subscription',
    };

    window.dataLayer.push({
        event: events[membershipType],
        membership_type: membershipType,
    });
};
export const Footer = ({ handleSubmit, isLetsGo, isSubmit, handleContinue }) => {
    return (
        <VendorAccountSetupStepFooter
            progress={''}
            isSubmit={isSubmit}
            isLetsGo={isLetsGo}
            handleSubmit={handleSubmit}
            hideStepCount={false}
            showBack={false}
            handleContinue={handleContinue}
            pearlConnectFooter
        />
    );
};

const VendorPaymentSuccess = () => {
    const { t } = useTranslation();
    const { authUser } = useAuthUserContext();
    const { link, view } = useTealium();
    const email = authUser.Email;
    const userName = authUser.Username;
    const { webstore } = useWebstoreContext();
    const vendorService = VendorService();
    const { onSubmit, currentStepCount, totalSteps } = useWebstoreSetupWizard();
    const storageService = StorageService();
    const { vendor } = useStore();
    useEffect(() => {
        const stepName = t(`Forms.${formName}.StepName`);
        const tealiumData = buildWebstoreSetupStepPageData(
            stepName,
            webstore,
            currentStepCount,
            totalSteps
        );

        view(tealiumData);
    }, []);

    useEffect(() => {
        storageService.remove('stripeData');
    }, []);

    // priceId is only set for a real plan purchase (Free/Essential/Premium via the
    // PLAN step). The premium-trial signup path has no Stripe price to report, so it
    // intentionally has no purchase pixel yet.
    const { subscriptionId, priceId, total } = vendor.stripeData || {};
    const isProduction =
        process.env.REACT_APP_VENDORCONNECT_URL === 'https://connect-app.davidsbridal.com';
    const bluecorePixelUrl =
        isProduction && priceId && subscriptionId && total != null && email && vendor.contactPhone
            ? buildBluecorePurchasePixelUrl({
                  email,
                  phone: vendor.contactPhone,
                  total,
                  orderId: subscriptionId,
                  productId: priceId,
              })
            : null;

    useEffect(() => {
        if (!isProduction) return;
        const membershipType = MEMBERSHIP_TYPES_BY_SELECTED_PLAN[webstore.SelectedPlan];
        if (!membershipType) return;
        trackConnectConversion(membershipType);
    }, []);

    const getVendorPlanData = async () => {
        const updatedVendor = await vendorService.getVendorByUsername(userName);
        if (updatedVendor) {
            vendor.updateVendor(updatedVendor);
        }
    };

    const defaultValue = webstore.PaymentSuccess;
    const formOptions = {
        mode: 'onChange',
        defaultValues: { PaymentSuccess: defaultValue },
    };
    const SuccessMessage = () => {
        return (
            <Container>
                <div>
                    <Text data-testid="welcome-subtitle-one">
                        {t(`Forms.${formName}.SuccessSubtitleOne`)}
                        <Email data-testid="welcome-email">{maskEmail(email)}</Email>
                    </Text>
                </div>
                <Text data-testid="welcome-subtitle-two">
                    {t(`Forms.${formName}.SuccessSubtitleTwo`)}
                </Text>
            </Container>
        );
    };

    const handleContinue = () => {
        getVendorPlanData();
    };
    const handleOnSubmit = (data) => {
        const tealiumData = {
            event_name: 'setup-step-advance-click',
            setup_step_number: 1,
            setup_step_total_step_count: totalSteps,
            setup_step_name: t(`Forms.${formName}.StepName`),
            setup_step_form_data: data,
            setup_step_patch_data: data,
            webstore_id: webstore.Id,
        };

        link(tealiumData);
        onSubmit(data);
    };

    return (
        <PearlForm
            formName={formName}
            formOptions={formOptions}
            onSubmit={(data) => handleOnSubmit(data)}
        >
            <VendorAccountSetupStepLayout
                header={
                    <NewVendorWebstoreSetupHeader
                        stepName={formName}
                        hideStepCount={true}
                        currentStepCount={currentStepCount}
                        totalSteps={totalSteps}
                        showMobileTopBar
                        pearlConnectStepTheme
                    />
                }
                formComponent={
                    <SuccessPage>
                        <SuccessMessage />
                    </SuccessPage>
                }
                footer={<Footer isLetsGo={true} handleContinue={handleContinue} />}
            />
            {bluecorePixelUrl && (
                <img hidden id="bluecore_purchase_pixel" src={bluecorePixelUrl} alt="" />
            )}
        </PearlForm>
    );
};

export { VendorPaymentSuccess };
